import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft } from 'lucide-react';
import { PinKeypad } from '@/components/ui/pin-keypad';
import { useFacebookPixel } from '@/hooks/useFacebookPixel';
import villatechBlueLogo from '@/assets/villatech-logo-blue.png';

interface RegistrationData {
  nombre: string;
  telefono: string;
  email: string;
  modeloCelular: string;
  normalizedId: string; // Generated from nombre (e.g., "roberto_carrillo")
  plan: 'bronce' | 'plata' | 'oro';
  billingPeriod: 'mensual' | 'trimestral' | 'anual';
}

// Removed card logos and gradients - using Login-style clean design


const RegistroMembresia: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const { trackCompleteRegistration } = useFacebookPixel();
  
  const [registrationData, setRegistrationData] = useState<RegistrationData | null>(null);
  const [step, setStep] = useState<'pin' | 'confirm'>('pin');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Get registration data from location state
    const data = location.state?.registrationData as RegistrationData;
    if (!data) {
      toast({
        title: 'Error',
        description: 'No se encontraron datos de registro. Por favor, completa el formulario de membresía.',
        variant: 'destructive',
      });
      navigate('/membresias');
      return;
    }
    setRegistrationData(data);
  }, [location.state, navigate, toast]);

  // Note: We don't redirect logged-in users here because this is a registration flow
  // The user should complete their registration independently

  // Auto-advance when PIN is complete
  useEffect(() => {
    if (step === 'pin' && pin.length === 6) {
      setTimeout(() => setStep('confirm'), 300);
    }
  }, [pin, step]);

  // Auto-submit when confirm PIN is complete
  useEffect(() => {
    if (step === 'confirm' && confirmPin.length === 6) {
      if (pin === confirmPin) {
        handleRegister();
      } else {
        toast({
          title: 'Los PINs no coinciden',
          description: 'Por favor intenta de nuevo',
          variant: 'destructive',
        });
        setConfirmPin('');
      }
    }
  }, [confirmPin, step]);

  const handleRegister = async () => {
    if (!registrationData) return;
    
    setLoading(true);

    try {
      const redirectUrl = `${window.location.origin}/login`;
      
      // Use synthesized email with normalizedId (derived from full name)
      const { error } = await supabase.auth.signUp({
        email: `${registrationData.normalizedId}@villatech.member`,
        password: pin,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            nombre: registrationData.nombre,
            telefono: registrationData.telefono,
            modelo_celular: registrationData.modeloCelular,
            plan: registrationData.plan,
            billing_period: registrationData.billingPeriod,
            username: registrationData.normalizedId,
            pin: pin, // Save PIN for admin recovery
          },
        },
      });

      if (error) {
        if (error.message.includes('User already registered')) {
          toast({
            title: 'Usuario ya registrado',
            description: 'Este usuario ya tiene una cuenta. Intenta iniciar sesión.',
            variant: 'destructive',
          });
          navigate('/login');
        } else {
          toast({
            title: 'Error al registrarse',
            description: error.message,
            variant: 'destructive',
          });
        }
        setConfirmPin('');
        setLoading(false);
        return;
      }

      // Track CompleteRegistration event
      const planPrices: Record<string, number> = {
        bronce: 12000,
        plata: 15000,
        oro: 18000,
      };
      trackCompleteRegistration({
        content_name: `Membresía ${registrationData.plan}`,
        value: planPrices[registrationData.plan] || 12000,
        currency: 'CRC',
      });

      toast({
        title: '¡Membresía activada!',
        description: 'Bienvenido a VillaTech. Redirigiendo a tu hub...',
      });

      navigate('/mi-membresia', { replace: true });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Ocurrió un error inesperado',
        variant: 'destructive',
      });
      setConfirmPin('');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !registrationData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-start justify-center pt-8 sm:pt-16 md:pt-24 p-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        {/* Registration card - matching Login design */}
        <div className="bg-zinc-900/80 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-zinc-800 shadow-2xl">
          {/* Navigation inside card */}
          <div className="mb-4">
            <button 
              onClick={() => {
                if (step === 'confirm') {
                  setConfirmPin('');
                  setStep('pin');
                } else {
                  navigate('/');
                }
              }}
              className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {step === 'pin' ? 'Volver' : 'Cambiar PIN'}
            </button>
          </div>

          {/* Logo */}
          <div className="text-center mb-6">
            <img 
              src={villatechBlueLogo} 
              alt="VillaTech" 
              className="h-12 md:h-14 mx-auto mb-4"
            />
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {step === 'pin' && (
                  <>
                    <h1 className="text-xl md:text-2xl font-bold text-white">Crea tu PIN</h1>
                    <p className="text-zinc-400 mt-2 text-sm md:text-base">Hola, {registrationData.nombre}</p>
                  </>
                )}
                {step === 'confirm' && (
                  <>
                    <h1 className="text-xl md:text-2xl font-bold text-white">Confirma tu PIN</h1>
                    <p className="text-zinc-400 mt-2 text-sm md:text-base">Ingresa los mismos 6 dígitos</p>
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          <AnimatePresence mode="wait">
            {step === 'pin' && (
              <motion.div
                key="pin"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="space-y-4"
              >
                {/* PIN Display - matching login style */}
                <div className="flex justify-center gap-2 mb-4">
                  {[...Array(6)].map((_, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.05 * idx }}
                      className={`w-10 h-10 md:w-12 md:h-12 rounded-xl border-2 flex items-center justify-center transition-all ${
                        pin.length > idx
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-zinc-700 bg-zinc-800/50'
                      }`}
                    >
                      {pin.length > idx && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-2.5 h-2.5 rounded-full bg-blue-400"
                        />
                      )}
                    </motion.div>
                  ))}
                </div>
                <PinKeypad
                  onNumberClick={(num) => pin.length < 6 && setPin(pin + num)}
                  onDelete={() => setPin(pin.slice(0, -1))}
                  variant="blue"
                />
              </motion.div>
            )}

            {step === 'confirm' && (
              <motion.div
                key="confirm"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="space-y-4"
              >
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Loader2 className="w-12 h-12 animate-spin text-blue-500 mb-4" />
                    <p className="text-white/60">Activando tu membresía...</p>
                  </div>
                ) : (
                  <>
                    {/* PIN Display - matching login style */}
                    <div className="flex justify-center gap-2 mb-4">
                      {[...Array(6)].map((_, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.05 * idx }}
                          className={`w-10 h-10 md:w-12 md:h-12 rounded-xl border-2 flex items-center justify-center transition-all ${
                            confirmPin.length > idx
                              ? 'border-blue-500 bg-blue-500/10'
                              : 'border-zinc-700 bg-zinc-800/50'
                          }`}
                        >
                          {confirmPin.length > idx && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-2.5 h-2.5 rounded-full bg-blue-400"
                            />
                          )}
                        </motion.div>
                      ))}
                    </div>
                    <PinKeypad
                      onNumberClick={(num) => confirmPin.length < 6 && setConfirmPin(confirmPin + num)}
                      onDelete={() => setConfirmPin(confirmPin.slice(0, -1))}
                      variant="blue"
                    />
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Login link */}
          <div className="mt-6 text-center">
            <p className="text-zinc-400 text-sm">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium">
                Inicia sesión
              </Link>
            </p>
          </div>

        </div>
      </motion.div>
    </div>
  );
};

export default RegistroMembresia;
